import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, XIcon } from 'lucide-react';
import type { GalleryImage } from '../types/showcase';
interface ImageLightboxProps {
  images: GalleryImage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
}
export function ImageLightbox({
  images,
  index,
  onClose,
  onChange
}: ImageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const current = open ? images[index] : null;
  const hasPrev = open && index > 0;
  const hasNext = open && index < images.length - 1;
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onChange(index - 1);
      if (e.key === 'ArrowRight' && index < images.length - 1) onChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, index, images.length, onClose, onChange]);
  return <AnimatePresence data-id="element-68" data-mp="1pcyhgdbf4af3">
      {current && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={current.caption ?? current.alt} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-50 flex flex-col bg-ink/95" onClick={onClose} data-id="element-69" data-mp="2d219mf3ohlnu">
          <div className="flex items-center justify-between px-4 py-3 text-white" onClick={e => e.stopPropagation()} data-id="element-70" data-mp="roonmllscsz2">
            <p className="text-sm font-medium" data-id="element-71" data-mp="wou91k1gpj8p">
              {current.caption}
              {images.length > 1 && <span className="text-white/60" data-id="element-72" data-mp="clevg34r9qwo"> · {index + 1} of {images.length}</span>}
            </p>
            <div className="flex items-center gap-1" data-id="element-73" data-mp="8kuxwyg9sla0">
              <a href={current.src} target="_blank" rel="noreferrer" className="inline-flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium hover:bg-white/10" data-id="element-74" data-mp="3t36klpaibit">
                <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-75" data-mp="3q24f2tbm6ki3" />
                <span className="hidden sm:inline" data-id="element-76" data-mp="3l0ox5a4qgu3x">Full size</span>
              </a>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-white/10" data-id="element-77" data-mp="3gzd22p33bgoc">
                <XIcon className="h-5 w-5" data-id="element-78" data-mp="1a9yehg7lzbhc" />
              </button>
            </div>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 sm:px-16" data-id="element-79" data-mp="1hthff1gt5pe7">
            <motion.img key={current.src} src={current.src} alt={current.alt} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-h-full max-w-full rounded-md object-contain shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-80" data-mp="1ms10kznkux5i" />
            {hasPrev && <button type="button" aria-label="Previous" onClick={e => {
          e.stopPropagation();
          onChange(index - 1);
        }} className="absolute left-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 sm:left-4" data-id="element-81" data-mp="13v9oba6gdiay">
                <ChevronLeftIcon className="h-6 w-6" data-id="element-82" data-mp="2mrusypwnkrak" />
              </button>}
            {hasNext && <button type="button" aria-label="Next" onClick={e => {
          e.stopPropagation();
          onChange(index + 1);
        }} className="absolute right-2 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 sm:right-4" data-id="element-83" data-mp="2httb28ayqy0o">
                <ChevronRightIcon className="h-6 w-6" data-id="element-84" data-mp="30r49jqortnf2" />
              </button>}
          </div>
        </motion.div>}
    </AnimatePresence>;
}